import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';
import Header from './Header';
import { Button } from './figma/FigmaComponents';

function SubscriptionCanceled() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const status = await configService.getSubscriptionStatus();
      setSubscriptionStatus(status);
      
      // If subscription is active, redirect to dashboard
      if (status?.status === 'active' || status?.status === 'trialing') {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Failed to check subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCallHistory = async () => {
    setExporting(true);
    setExportError('');
    
    try {
      const conversations = await configService.getConversations(1000);
      
      // Convert to CSV format
      const csvData = convertToCSV(conversations.conversations || []);
      
      // Create download
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-history-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      setExportError('Failed to export call history. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const convertToCSV = (conversations) => {
    const headers = ['Date', 'Time', 'Caller', 'Duration', 'Type', 'Status', 'Summary'];
    const rows = conversations.map(conv => {
      const date = new Date(conv.created_at * 1000);
      return [
        date.toLocaleDateString(),
        date.toLocaleTimeString(),
        conv.caller_phone || 'Unknown',
        conv.duration || 'N/A',
        conv.conversation_type || 'call',
        conv.status || 'completed',
        (conv.summary || '').replace(/"/g, '""')
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    return csvContent;
  };

  const getDaysRemaining = () => {
    // Use days_remaining from API if available
    if (subscriptionStatus?.days_remaining !== undefined) {
      return subscriptionStatus.days_remaining;
    }
    
    // Fallback to client-side calculation
    if (!subscriptionStatus?.canceled_at) return 15;
    
    const canceledDate = new Date(subscriptionStatus.canceled_at * 1000);
    const deletionDate = new Date(canceledDate);
    deletionDate.setDate(deletionDate.getDate() + 15);
    
    const now = new Date();
    const daysLeft = Math.ceil((deletionDate - now) / (1000 * 60 * 60 * 24));
    
    return Math.max(0, daysLeft);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining();

  return (
    <>
      <Header />
      
      <div className="pt-24 pb-16 px-6 max-w-3xl mx-auto">
        <div className="bg-white rounded shadow-lg border p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h1 className="text-3xl text-gray-900 mb-2" style={{ fontWeight: 700 }}>
              Subscription Canceled
            </h1>
            <p className="text-lg text-gray-600">
              Your subscription has been canceled
            </p>
          </div>

          <div className="bg-red-50 border-2 border-red-200 rounded p-6 mb-8">
            <h2 className="text-xl text-red-900 mb-2" style={{ fontWeight: 700 }}>
              ⏰ {daysRemaining} Days Remaining
            </h2>
            <p className="text-red-800">
              You have <strong>{daysRemaining} days</strong> to reactivate your subscription before your data is permanently deleted.
            </p>
            {daysRemaining <= 5 && (
              <p className="text-red-900 mt-2" style={{ fontWeight: 700 }}>
                ⚠️ Your data will be deleted soon. Please take action immediately.
              </p>
            )}
          </div>

          <div className="space-y-6 mb-8">
            <div>
              <h3 className="text-lg text-gray-900 mb-2" style={{ fontWeight: 700 }}>
                What happens next?
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Your AI assistant is no longer active and cannot receive calls</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Your assistant phone number will be released in {daysRemaining} days</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>All configurations and call history will be deleted in {daysRemaining} days</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>You can reactivate anytime within the next {daysRemaining} days</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg text-gray-900 mb-2" style={{ fontWeight: 700 }}>
                Export Your Data
              </h3>
              <p className="text-gray-700 mb-4">
                Download your call history before it's permanently deleted.
              </p>
              <Button
                onClick={handleExportCallHistory}
                disabled={exporting}
                variant="primary"
              >
                {exporting ? 'Exporting...' : '📥 Export Call History'}
              </Button>
              {exportError && (
                <p className="text-red-600 mt-2 text-sm">{exportError}</p>
              )}
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg text-gray-900 mb-4 text-center" style={{ fontWeight: 700 }}>
              Ready to come back?
            </h3>
            <Button
              onClick={() => navigate('/plans')}
              variant="success"
              fullWidth
            >
              🔄 Reactivate Subscription
            </Button>
            <p className="text-center text-sm text-gray-600 mt-3">
              Choose a plan to restore your assistant and data
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default SubscriptionCanceled;
